import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { articles } from "@/mocks/articles";
import {
  SITE_URL,
  SITE_NAME,
  FAVICON_URL,
  LAST_MODIFIED,
  buildWebSiteSchema,
  buildOrganizationSchema,
  buildArticleSchema,
  buildFaqSchema,
  defaultFaqs,
} from "@/seo/schema";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr: "name" | "property", key: string) {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeLink(rel: string) {
  document.head.querySelector(`link[rel="${rel}"]`)?.remove();
}

function setJsonLd(id: string, data: Record<string, unknown>) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function removeJsonLd(id: string) {
  document.getElementById(id)?.remove();
}

const KEYWORD_MAP: Record<string, string> = {
  "cssbuy-spreadsheet-guide":
    "CSSBuy Spreadsheet, CSSBuy guide, CSSBuy products, CSSBuy QC, CSSBuy links",
  "cssbuy-review-2026":
    "CSSBuy review, CSSBuy legit, CSSBuy pros and cons, CSSBuy buyer guide",
  "cssbuy-shipping-cost":
    "CSSBuy shipping cost, CSSBuy shipping rates, CSSBuy shipping methods, CSSBuy shipping calculator",
  "cssbuy-qc-guide":
    "CSSBuy QC, CSSBuy quality control, CSSBuy QC photos, CSSBuy QC checklist",
  "cssbuy-coupon-guide":
    "CSSBuy coupon, CSSBuy discount code, CSSBuy deals, CSSBuy promo",
  "best-shoes-cssbuy":
    "CSSBuy shoes, best shoes CSSBuy, CSSBuy sneakers, CSSBuy shoe spreadsheet",
  "cssbuy-safety-guide":
    "CSSBuy safety, CSSBuy legit, CSSBuy scam check, CSSBuy secure shopping",
  "cssbuy-vs-agents":
    "CSSBuy vs Superbuy, CSSBuy comparison, shopping agents, CSSBuy alternatives",
  "cssbuy-warehouse-guide":
    "CSSBuy warehouse, CSSBuy storage, CSSBuy consolidation, CSSBuy repackaging",
  "cssbuy-beginner-guide":
    "CSSBuy beginner guide, CSSBuy how to, CSSBuy tutorial, CSSBuy first order",
};

const HOME_META = {
  title: "CSSBuy Spreadsheet — Find Products, Compare & Organize | cssbuybr.skin",
  description:
    "Independent CSSBuy resource. Explore organized product links, categories, buying guides, QC information and shipping resources for easier CSSBuy research.",
  keywords:
    "CSSBuy Spreadsheet, CSSBuy review, CSSBuy shipping, CSSBuy QC, CSSBuy products, CSSBuy guide",
};

function applyHome() {
  document.title = HOME_META.title;
  upsertMeta("name", "description", HOME_META.description);
  upsertMeta("name", "keywords", HOME_META.keywords);
  upsertLink("canonical", `${SITE_URL}/`);

  upsertMeta("property", "og:title", HOME_META.title);
  upsertMeta("property", "og:description", HOME_META.description);
  upsertMeta("property", "og:url", `${SITE_URL}/`);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:image", FAVICON_URL);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", HOME_META.title);
  upsertMeta("name", "twitter:description", HOME_META.description);
  upsertMeta("name", "twitter:image", FAVICON_URL);

  removeMeta("property", "article:published_time");
  removeMeta("property", "article:modified_time");

  setJsonLd(
    "ld-website",
    buildWebSiteSchema(SITE_URL, SITE_NAME, HOME_META.description),
  );
  setJsonLd(
    "ld-organization",
    buildOrganizationSchema(SITE_URL, SITE_NAME, FAVICON_URL),
  );
  removeJsonLd("ld-article");
  removeJsonLd("ld-faq");
}

function applyArticle(slug: string) {
  const article = articles.find((a) => a.slug === slug);
  if (!article) {
    applyNotFound();
    return;
  }

  const title = article.title.en;
  const description = article.excerpt.en;
  const keywords =
    KEYWORD_MAP[slug] || "CSSBuy Spreadsheet, CSSBuy guide, CSSBuy products";
  const canonical = `${SITE_URL}/article/${slug}`;
  const fullTitle = `${title} | ${SITE_NAME}`;

  document.title = fullTitle;
  upsertMeta("name", "description", description);
  upsertMeta("name", "keywords", keywords);
  upsertLink("canonical", canonical);

  upsertMeta("property", "og:title", fullTitle);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", canonical);
  upsertMeta("property", "og:type", "article");
  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:image", article.image);
  upsertMeta("property", "article:published_time", article.date);
  upsertMeta("property", "article:modified_time", LAST_MODIFIED);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", fullTitle);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", article.image);

  setJsonLd(
    "ld-article",
    buildArticleSchema(article, SITE_URL, SITE_NAME, FAVICON_URL),
  );
  setJsonLd("ld-faq", buildFaqSchema(defaultFaqs));
  removeJsonLd("ld-website");
  removeJsonLd("ld-organization");
}

function applyNotFound() {
  document.title = "Page Not Found | CSSBuy Spreadsheet";
  upsertMeta("name", "description", "The requested page could not be found.");
  upsertMeta("name", "keywords", "");
  removeLink("canonical");
  removeMeta("property", "og:image");
  removeMeta("name", "twitter:image");
  removeMeta("property", "article:published_time");
  removeMeta("property", "article:modified_time");
  removeJsonLd("ld-website");
  removeJsonLd("ld-organization");
  removeJsonLd("ld-article");
  removeJsonLd("ld-faq");
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === "/" || pathname === "") {
      applyHome();
      return;
    }

    const match = pathname.match(/^\/article\/([^/]+)/);
    if (match) {
      applyArticle(match[1]);
      return;
    }

    applyNotFound();
  }, [pathname]);

  return null;
}